import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "./useTokenGetter";
import { fetchMemberships } from "../services/onboardingService";
import type { WorkspaceReferral } from "../types/onboarding.types";

export type WorkspaceReferralStatus = "loading" | "ready" | "failed";

export interface WorkspaceReferralState {
  /**
   * The partner whose link this practice arrived through, or `null`.
   *
   * `null` for most practices, and it is an answer rather than a gap: coming
   * to Naurist directly is the ordinary way in. It is also `null` while the
   * read is still out, which is why `status` is carried beside it — a caller
   * that cannot tell "not referred" from "not known yet" would ask the coupon
   * lookup the wrong question.
   */
  referral: WorkspaceReferral | null;
  /** The code on its own, which is all the coupon lookup wants. */
  referralCode: string | null;
  status: WorkspaceReferralStatus;
}

export const MEMBERSHIPS_QUERY_KEY = ["onboarding", "memberships"] as const;

/**
 * The partner referral recorded against this practice.
 *
 * Off `GET /api/v1/onboarding/workspace/`, which is the only endpoint that
 * publishes it. The code was spent at sign-up — `POST` is the one call that
 * can record one — and from then on it is simply a fact about the workspace,
 * carried on every membership row and visible to every member.
 *
 * What wants it is the coupon lookup: `GET /api/v1/referrals/` answers a
 * partner's own coupon only when asked by their code, so without this the
 * dialog can only ever show Naurist's own. See `fetchReferralOffer`.
 *
 * Backed by TanStack Query under its own key, so the several screens that ask
 * for it share one request and a practice's referral is read once a session
 * rather than once a checkout. It does not change: a practice is referred at
 * most once, and never re-referred.
 *
 * A failure is reported as `failed` and **not** as "not referred". The
 * difference matters at the call site: a read that fell over should leave the
 * coupon dialog asking for whatever it can get, not quietly decide this
 * practice has no partner.
 *
 * Must be used inside a Clerk provider.
 */
export function useWorkspaceReferral(): WorkspaceReferralState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: MEMBERSHIPS_QUERY_KEY,
    queryFn: ({ signal }) => fetchMemberships(getToken, signal),
    // It is settled at sign-up and never moves again, so re-reading it on
    // every mount is a request that can only ever confirm what is already
    // known.
    staleTime: Infinity,
  });

  useEffect(() => {
    if (query.isError) {
      console.error("workspace memberships failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  // The first membership that carries one. A user may hold more than one
  // membership, and only one practice can have been referred through any given
  // link; picking the first with a referral on it is the only reading that
  // does not need a notion of "the current workspace" this app does not have
  // yet.
  const referral =
    query.data?.map((membership) => membership.workspace.referral)
      .find((candidate): candidate is WorkspaceReferral => candidate !== null) ??
    null;

  return {
    referral,
    referralCode: referral?.referralCode ?? null,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}

export default useWorkspaceReferral;
